import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon as SearchIcon } from '@heroicons/react/24/solid';
// @ts-ignore
import debounce from 'lodash.debounce';
import Overlay from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/overlay';
import { AddressSearchType } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/checkout/components/steps/sections/addresses/types';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { useAccount } from 'frontastic';
import { Address } from '@loomstateorg/shared-frontend/types/account';
import Typography from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/typography';
import SearchItemAddress from './search-item';

interface Props {
  type: 'shipping' | 'billing';
  addressType: string;
  hideDefaultAddress?: boolean;
  onSelected?: (selected: Address) => void;
  onCancel?: () => void;
}

const SearchAddress: React.FC<Props> = ({ type, addressType, hideDefaultAddress = false, onSelected, onCancel }) => {
  const { getShippingAddress } = useAccount();
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Address[]>([]);

  const { formatMessage } = useFormat({ name: 'common' });
  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchResults = useCallback(
    async (addressType: string) => {
      setLoading(true);
      const defaultType = addressType === AddressSearchType.SAVED ? undefined : type;
      const results = (await getShippingAddress(query, addressType, defaultType)) as unknown as Address[];
      // Only filter default addresses when the 'hideDefaultAddress' variable is true and only for saved addresses
      const addresses = results.filter(
        (address) =>
          !(
            addressType === AddressSearchType.SAVED &&
            hideDefaultAddress &&
            (address.isDefaultShippingAddress || address.isDefaultBillingAddress)
          ),
      );
      setItems(addresses ?? []);
      setLoading(false);
    },
    [query, getShippingAddress, type, hideDefaultAddress],
  );

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const updateQuery = useCallback(
    debounce((value: string) => (value.length > 2 ? setQuery(value) : setQuery('')), 350),
    [],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      updateQuery(e.target.value);
    },
    [updateQuery],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!value) return;
      input.current?.blur();
    },
    [value],
  );

  const handleSelection = useCallback(
    (address: Address) => {
      onSelected?.(address);
    },
    [onSelected],
  );

  useEffect(() => {
    if (addressType) {
      setItems([]);
      fetchResults(addressType);
    }
  }, [fetchResults, addressType]);

  return (
    <>
      <div data-component-group="form-control" data-testid="atom-search-address" className="relative mb-24 border border-neutral-400 p-20 shadow-400">
        <div className="flex-end relative mb-30 rounded-xxl border border-neutral-400 bg-site">
          <form
            className="quick-search relative flex w-full items-stretch"
            ref={form}
            onSubmit={onSubmit}
            data-testid="atom-search-address-form"
          >
            <button
              type="submit"
              data-testid="atom-search-address-submit"
              title={formatMessage({ id: 'search', defaultMessage: 'Search' })}
              className={`shrink-0 rounded-xxl p-10 transition`}
            >
              <SearchIcon className="size-15 fill-primary-regular stroke-0" />
            </button>
            <input
              ref={input}
              data-testid="atom-search-address-input"
              className="box-content w-full rounded-xxl border-none !bg-site p-0 px-10 font-secondaryThin text-12 transition placeholder:text-primary-regular focus:outline-none"
              value={value}
              onChange={onChange}
              placeholder={
                type === 'billing'
                  ? formatMessage({
                      id: 'search.addresses.billing.placeholder',
                      defaultMessage: 'Search by Location Name or Cost Center ID',
                    })
                  : formatMessage({
                      id: 'search.addresses.placeholder',
                      defaultMessage: 'Search by Location Name or Location ID',
                    })
              }
            />
          </form>
        </div>
        <hr className="relative mb-30 border-neutral-400" />
        {loading && <Overlay />}
        {items.slice(0, 10).map((address, index) => (
          <SearchItemAddress key={address.addressId} address={address} onClick={handleSelection} index={index} />
        ))}
        {!loading && items.length == 0 && (
          <div>
            <Typography
              as="span"
              fontFamily="primaryThin"
              fontSize={14}
              align="center"
              className="my-10 italic"
              data-testid="atom-search-address-empty"
            >
              {formatMessage({ id: 'no.addresses', defaultMessage: 'No addresses found' })}
            </Typography>
          </div>
        )}
        <div className="flex justify-center">
          <button
            type="button"
            title="Cancel"
            data-testid="atom-search-address-cancel"
            className="shrink-0 px-16 py-10 font-primaryThin text-11 underline hover:cursor-pointer"
            onClick={onCancel}
          >
            {formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
          </button>
        </div>
      </div>
    </>
  );
};

export default SearchAddress;
